// src/libs/affiliateAttribution.js

/**
 * Attribution d’affiliation (LAST_TOUCH + landedAt sur la combo courante).
 * - Capture ?source, ?tracker (ou ?track) et utm_* depuis l'URL (query + hash)
 * - Stocke en localStorage avec fenêtre glissante: `ts` mis à jour à CHAQUE passage
 * - `landedAt` = 1er passage pour **la combinaison actuelle** (source/tracker/utm_*)
 * - Si la combinaison change → nouveau `landedAt`
 * - Nettoie l'URL après capture
 */

const TOKEN = process.env.REACT_APP_TOKEN_NAME || "voissa";
const LS_KEY = `${TOKEN}_aff_attrib`; // { source, tracker, utm_*, ts, landedAt }
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

const safeParse = (raw) => { try { return JSON.parse(raw); } catch { return null; } };
const isExpired = (ts) => !ts || (Date.now() - ts) > TTL_MS;

const save = (obj) => localStorage.setItem(LS_KEY, JSON.stringify(obj));

const load = () => {
  const raw = localStorage.getItem(LS_KEY);
  if (!raw) return null;
  const p = safeParse(raw);
  if (!p || isExpired(p.ts)) { localStorage.removeItem(LS_KEY); return null; }
  return p;
};

export const clearAffiliateAttribution = () => localStorage.removeItem(LS_KEY);

const readParams = () => {
  const search = new URLSearchParams(window.location.search);
  const hashStr = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  const hash = new URLSearchParams(hashStr);
  const pick = (k, alias) => search.get(k) || hash.get(k) || (alias ? (search.get(alias) || hash.get(alias)) : "");
  return {
    source:      (pick("source") || "").trim(),
    tracker:     (pick("tracker", "track") || "").trim(),
    utm_source:  (pick("utm_source") || "").trim(),
    utm_medium:  (pick("utm_medium") || "").trim(),
    utm_campaign:(pick("utm_campaign") || "").trim(),
    utm_term:    (pick("utm_term") || "").trim(),
    utm_content: (pick("utm_content") || "").trim(),
    disc: pick("disc") || "",
    hist: pick("hist") || ""
  };
};
//,"utm_source","utm_medium","utm_campaign","utm_term","utm_content"
const cleanUrl = () => {
  const url = new URL(window.location.href);
  const keys = ["source","tracker","track","disc","hist"];
  keys.forEach(k => url.searchParams.delete(k));
  const hp = new URLSearchParams(url.hash.replace(/^#/, ""));
  keys.forEach(k => hp.delete(k));
  url.hash = hp.toString() ? `#${hp.toString()}` : "";
  window.history.replaceState(null, document.title, url.toString());
};

// comparaison stricte des champs d'attribution
const sameCombo = (a = {}, b = {}) => (
  (a.source || "")      === (b.source || "") &&
  (a.tracker || "")     === (b.tracker || "") &&
  (a.utm_source || "")  === (b.utm_source || "") &&
  (a.utm_medium || "")  === (b.utm_medium || "") &&
  (a.utm_campaign || "")=== (b.utm_campaign || "") &&
  (a.utm_term || "")    === (b.utm_term || "") &&
  (a.utm_content || "") === (b.utm_content || "")
);

/**
 * Init: si combo identique → on garde landedAt, on met à jour ts.
 *       si combo différente → on remplace les valeurs ET landedAt = now.
 *       on ne stocke que si `source` est présent.
 */
export const initAffiliateAttribution = () => {
  const q = readParams();
  const hasAny =
    !!(q.source || q.tracker || q.utm_source || q.utm_medium || q.utm_campaign || q.utm_term || q.utm_content);
  if (!hasAny) return;

  if (!q.source) { cleanUrl(); return; } // source obligatoire

  const now = Date.now();
  const prev = load();
  const incoming = {
    source: q.source,
    tracker: q.tracker,
    utm_source: q.utm_source,
    utm_medium: q.utm_medium,
    utm_campaign: q.utm_campaign,
    utm_term: q.utm_term,
    utm_content: q.utm_content
  };

  const landedAt = prev && sameCombo(prev, incoming) ? (prev.landedAt || prev.ts || now) : now;

  save({ ...incoming, landedAt, ts: now });
  cleanUrl();
};

export const getSignupAttribution = () => {
  const aff = load() || {};
  return {
    source: aff.source || "",
    tracker: aff.tracker || "",
    utm_source: aff.utm_source || "",
    utm_medium: aff.utm_medium || "",
    utm_campaign: aff.utm_campaign || "",
    utm_term: aff.utm_term || "",
    utm_content: aff.utm_content || "",
    landedAt: aff.landedAt || aff.ts || null
  };
};
